import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { Box, Typography, Stack } from "@mui/material";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import { motion } from "framer-motion";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

const BRAND = { navy: "#00357a", gold: "#c9a96e" };

const STATUS_COLOR = {
  High:   "#dc2626",
  Low:    "#b47800",
  Normal: "#16a34a",
};

function parseRef(ref) {
  if (!ref) return null;
  const range = ref.match(/([0-9.]+)\s*[-–]\s*([0-9.]+)/);
  if (range) return { low: parseFloat(range[1]), high: parseFloat(range[2]) };
  const lt = ref.match(/<\s*([0-9.]+)/);
  if (lt)   return { low: null, high: parseFloat(lt[1]) };
  const gt = ref.match(/>\s*([0-9.]+)/);
  if (gt)   return { low: parseFloat(gt[1]), high: null };
  return null;
}

// 2026-10-21 fix ("My Results" ecosystem plan, Phase 1, Stage 2) — exported
// so MyResults.js can reuse this exact reference-range-band chart logic for
// its own per-value sparkline (inside the detail modal) without duplicating
// it. Purely additive (just an export keyword) — this component's own
// existing usage (the default-exported TrendsPanel, mounted in
// BotInterface.js) is completely unchanged.
//
// 2026-10-21 fix (ui-ux-checker finding) — `statusColor` is a new,
// OPTIONAL param (defaults to this file's own STATUS_COLOR, so
// BotInterface's existing call — which never passes it — is byte-for-
// byte unaffected). MyResults.js passes its own statusColor map so the
// same value's color agrees between its gauge-bar list and this reused
// chart, instead of the same signal reading red in one place and amber/
// violet in another on one page — a real trust-relevant inconsistency in
// a healthcare context, not just a style nit.
export function buildDatasets(chart, statusColor = STATUS_COLOR) {
  const { points, ref, test } = chart;
  const parsed   = parseRef(ref);
  const datasets = [];

  if (parsed && parsed.low != null && parsed.high != null) {
    datasets.push({
      label: "_refHigh", data: points.map(() => parsed.high),
      borderColor: "rgba(220,38,38,0.3)", borderDash: [5, 4], borderWidth: 1,
      pointRadius: 0, fill: "+1", backgroundColor: "rgba(220,38,38,0.07)", tension: 0,
    });
    datasets.push({
      label: "_refLow", data: points.map(() => parsed.low),
      borderColor: "rgba(37,99,235,0.3)", borderDash: [5, 4], borderWidth: 1,
      pointRadius: 0, fill: false, tension: 0,
    });
  } else if (parsed?.high != null) {
    datasets.push({
      label: "_refMax", data: points.map(() => parsed.high),
      borderColor: "rgba(220,38,38,0.4)", borderDash: [5, 4], borderWidth: 1.5,
      pointRadius: 0, fill: false, tension: 0,
    });
  } else if (parsed?.low != null) {
    datasets.push({
      label: "_refMin", data: points.map(() => parsed.low),
      borderColor: "rgba(37,99,235,0.4)", borderDash: [5, 4], borderWidth: 1.5,
      pointRadius: 0, fill: false, tension: 0,
    });
  }

  datasets.push({
    label: test,
    data:  points.map(p => p.value),
    borderColor:          BRAND.navy,
    borderWidth:          2.5,
    pointBackgroundColor: points.map(p => statusColor[p.status] || BRAND.navy),
    pointBorderColor:     points.map(p => statusColor[p.status] || BRAND.navy),
    pointRadius:          6,
    pointHoverRadius:     8,
    tension:              0.35,
    fill:                 false,
    order:                0,
  });

  return datasets;
}

export function chartOptions(chart, isDark) {
  const { unit, test } = chart;
  const grid  = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)";
  const ticks = isDark ? "#94a3b8" : "#64748b";
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            if (ctx.dataset.label === test) {
              const pt = chart.points[ctx.dataIndex];
              return ` ${ctx.parsed.y} ${unit}  —  ${pt.status}`;
            }
            return null;
          },
        },
        backgroundColor: isDark ? "#1e293b" : "#fff",
        titleColor:      isDark ? "#f1f5f9" : "#0d1b2e",
        bodyColor:       isDark ? "#94a3b8" : "#64748b",
        borderColor:     isDark ? "#334155" : "#e2e8f0",
        borderWidth: 1, padding: 10,
        filter: (item) => !item.dataset.label.startsWith("_ref"),
      },
    },
    scales: {
      x: {
        ticks:  { color: ticks, font: { size: 10.5 }, maxRotation: 30 },
        grid:   { color: grid },
        border: { color: "transparent" },
      },
      y: {
        ticks: { color: ticks, font: { size: 10.5 }, callback: v => `${v}${unit ? " " + unit : ""}` },
        grid:  { color: grid },
        border:{ color: "transparent" },
      },
    },
  };
}

// ── Exported component — receives data as props, renders inline ───────────────
// `statusColor` — optional, defaults to STATUS_COLOR (BotInterface's
// existing call never passes it, so its rendering is unchanged). See
// buildDatasets' own comment above for why this exists.
export default function TrendsPanel({ charts, visitCount, isDark, border, textPrimary, textSecondary, statusColor = STATUS_COLOR }) {
  const cardBg = isDark ? "#1e293b" : "#ffffff";

  return (
    <Box sx={{ mt: 4 }}>
      {/* Section divider */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Box sx={{ flex: 1, height: "1px",
          background: `linear-gradient(to right, ${BRAND.gold}, transparent)` }} />
        <ShowChartIcon sx={{ color: BRAND.gold, fontSize: 18 }} />
        <Typography sx={{ fontSize: 12.5, fontWeight: 800, letterSpacing: "0.15em",
          color: isDark ? BRAND.gold : BRAND.navy, whiteSpace: "nowrap" }}>
          LONGITUDINAL TREND CHARTS
        </Typography>
        <Box sx={{ fontSize: 11.5, color: textSecondary,
          bgcolor: isDark ? "#0f172a" : "#f1f5f9",
          border: `1px solid ${border}`, borderRadius: 1, px: 1, py: 0.2 }}>
          {visitCount} visit{visitCount !== 1 ? "s" : ""}
        </Box>
        <Box sx={{ flex: 1, height: "1px",
          background: `linear-gradient(to left, ${BRAND.gold}, transparent)` }} />
      </Box>

      {/* Chart grid — 2 columns on desktop */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
        {charts.map((chart, idx) => {
          const lastPt    = chart.points[chart.points.length - 1];
          const accentClr = chart.has_abnormal
            ? (statusColor[lastPt.status] || statusColor.High)
            : statusColor.Normal;

          return (
            <motion.div
              key={chart.test}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.07, duration: 0.35 }}
            >
              <Box sx={{
                bgcolor:       cardBg,
                border:        `1px solid ${border}`,
                borderLeft:    `4px solid ${accentClr}`,
                borderRadius:  2,
                p:             2.5,
                display:       "flex",
                flexDirection: "column",
                boxShadow:     "0 2px 12px rgba(0,0,0,0.04)",
              }}>
                {/* Card header */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
                  <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: textPrimary,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "60%" }}>
                    {chart.test}
                  </Typography>
                  <Stack direction="row" alignItems="center" gap={0.75}>
                    <Box sx={{ width: 9, height: 9, borderRadius: "50%",
                      bgcolor: statusColor[lastPt.status] || BRAND.navy }} />
                    <Typography sx={{ fontSize: 14.5, fontWeight: 700,
                      color: statusColor[lastPt.status] }}>
                      {lastPt.status.toUpperCase()}
                    </Typography>
                    <Typography sx={{ fontSize: 14.5, color: textSecondary }}>
                      {lastPt.value}{chart.unit ? " " + chart.unit : ""}
                    </Typography>
                  </Stack>
                </Stack>

                {chart.ref && (
                  <Typography sx={{ fontSize: 13.5, color: textSecondary, mb: 1 }}>
                    Reference: {chart.ref}{chart.unit ? " " + chart.unit : ""}
                  </Typography>
                )}

                <Box sx={{ position: "relative", height: 200 }}>
                  <Line
                    data={{ labels: chart.points.map(p => p.date_label), datasets: buildDatasets(chart, statusColor) }}
                    options={chartOptions(chart, isDark)}
                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                  />
                </Box>
              </Box>
            </motion.div>
          );
        })}
      </Box>

      <Typography sx={{ fontSize: 11.5, color: textSecondary, mt: 2.5, textAlign: "center", lineHeight: 1.6 }}>
        Showing the {charts.length} most clinically significant biomarker{charts.length !== 1 ? "s" : ""} across {visitCount} saved visit{visitCount !== 1 ? "s" : ""}, ordered chronologically by collection date.
      </Typography>
    </Box>
  );
}
